// Frontend entry point.
//
// App Bridge v4 no longer uses a React Provider. Instead it auto-initializes
// from the <script src="…app-bridge.js" data-api-key="…"> tag in index.html
// and exposes a global `window.shopify`. React components call
// `useAppBridge()` (from @shopify/app-bridge-react) to get a typed handle
// to it.
//
// That leaves just two wrappers we set up here:
//   1. Polaris <AppProvider/> — provides Polaris design tokens, i18n, and
//      the portal/focus containers that Polaris components rely on.
//   2. react-router-dom <BrowserRouter/> — our own client-side routes
//      (onboarding wizard vs dashboard split in Phase D).
//
// Host detection: Shopify admin passes `?host=<base64>` when loading us in
// its iframe. App Bridge (the CDN script) reads it automatically from the
// URL — we don't need to pass it manually. We still check for it to
// distinguish "embedded-in-admin" from "dev tab / direct visit" and render
// a friendly fallback in the latter case.

import React from "react";
import ReactDOM from "react-dom/client";
import { AppProvider as PolarisAppProvider } from "@shopify/polaris";
import enTranslations from "@shopify/polaris/locales/en.json";
import { BrowserRouter } from "react-router-dom";
import "@shopify/polaris/build/esm/styles.css";

import App from "./App";
import { NotEmbeddedFallback } from "./NotEmbeddedFallback";
import { ErrorBoundary } from "./ErrorBoundary";

const host = new URLSearchParams(window.location.search).get("host");

const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error("#root not found in index.html (this should be impossible)");
}

const root = ReactDOM.createRoot(rootEl);

if (!host) {
  // Dev/direct visit: no host query param → friendly fallback explaining
  // this is meant to be opened from Shopify admin. Still inside Polaris
  // provider so the styling is consistent.
  root.render(
    <PolarisAppProvider i18n={enTranslations}>
      <NotEmbeddedFallback />
    </PolarisAppProvider>,
  );
} else {
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <PolarisAppProvider i18n={enTranslations}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </PolarisAppProvider>
      </ErrorBoundary>
    </React.StrictMode>,
  );
}
